import { Button, Modal, Table, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
  CreateSceneFromCaseFlowState,
  CreateSceneFromJiraState,
} from '@/apis/simulation';

/** 批量生成失败明细的单条：``code`` 为 Jira key 或 Case ID。 */
export interface BatchFailureItem {
  code: string;
  error: string;
}

type BatchState = CreateSceneFromJiraState | CreateSceneFromCaseFlowState;

/** 取消收口时后端给「未处理」条目填的占位原因（见 ``scene_batch_service._summary``）。 */
const CANCELLED_PLACEHOLDER = '本批已取消，未处理';

/**
 * 从批量生成任务的 ``result.results`` 里收集失败项。
 * Jira 路径的 code 字段是 ``key``，Case-Flow 路径是 ``caseId``，其余字段一致。
 *
 * 取消本批时未处理的条目会被后端标成 ``success=false`` + 上面那句占位原因——
 * 那不是「失败」，故在进明细前摘掉（条数由 ``countCancelledSkips`` 单独给）。
 */
export function collectBatchFailures(state: BatchState | null): BatchFailureItem[] {
  const results = state?.result?.results ?? [];
  return results
    .filter(r => !r.success && (r.error || '') !== CANCELLED_PLACEHOLDER)
    .map(r => ({
      code: ('key' in r ? r.key : r.caseId) || '-',
      error: r.error || '未知错误（后端未返回错误信息）',
    }));
}

/** 本批因取消而**没轮到处理**的条数（不是失败，别混进失败明细）。 */
export function countCancelledSkips(state: BatchState | null): number {
  const results = state?.result?.results ?? [];
  return results.filter(r => !r.success && (r.error || '') === CANCELLED_PLACEHOLDER).length;
}

const CSV_HEADER = ['case', '失败原因'];

const csvEscape = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** 前端本地导出（数据已在 ``result.results`` 里，无需再走后端；不占服务器资源）。 */
export function downloadFailures(items: BatchFailureItem[]) {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
    + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const lines = [
    CSV_HEADER.map(csvEscape).join(','),
    ...items.map(it => [csvEscape(it.code), csvEscape(it.error)].join(',')),
  ];
  // BOM 让 Excel 直接按 UTF-8 打开，中文原因不乱码。
  const blob = new Blob(['﻿' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `scene-batch-failures-${stamp}.csv`;
  a.click();
  // 立刻 revoke 在部分浏览器会让下载拿不到内容，挪到下一个宏任务。
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

const columns: ColumnsType<BatchFailureItem> = [
  {
    title: 'Case',
    dataIndex: 'code',
    width: 260,
    render: (code: string) => <Tag>{code}</Tag>,
  },
  {
    title: '失败原因',
    dataIndex: 'error',
    render: (error: string) => (
      <span style={{ color: '#cf1322', wordBreak: 'break-all' }}>{error}</span>
    ),
  },
];

interface Props {
  open: boolean;
  items: BatchFailureItem[];
  onClose: () => void;
}

/** 「场景批量生成」失败明细弹窗：逐条展示原因 + 下载 CSV。 */
const BatchFailureModal: React.FC<Props> = ({ open, items, onClose }) => (
  <Modal
    open={open}
    title={`失败明细（${items.length} 个）`}
    width={760}
    onCancel={onClose}
    destroyOnHidden
    footer={[
      <Button
        key="download"
        icon={<DownloadOutlined />}
        onClick={() => downloadFailures(items)}
        disabled={items.length === 0}
      >
        下载 CSV
      </Button>,
      <Button key="close" type="primary" onClick={onClose}>关闭</Button>,
    ]}
  >
    <Table<BatchFailureItem>
      rowKey={(r, i) => `${r.code}-${i}`}
      size="small"
      columns={columns}
      dataSource={items}
      pagination={{ pageSize: 20, showSizeChanger: false, showTotal: t => `共 ${t} 条` }}
      scroll={{ y: 420 }}
    />
  </Modal>
);

export default BatchFailureModal;